import React, {
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import { GridGenerator } from "@actinc/dls/components";
import { AlertContext } from "@actinc/dls/context";
import { Plus as PlusIcon } from "@actinc/dls/icons";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";
import { useForm, useFormState } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { useClient, useMutation } from "urql";

import { UserContext } from "../../../userProvider";
import { UsersContext } from "../../../usersProvider";
import { ProgramsContext } from "../../../programsProvider";
import { MetadataConfigurationContext } from "../../../metadataConfigurationProvider";
import { getRHFFieldOptionLabel } from "../../../services/formUtils";
import { useGetMetadataConfigurationOptions } from "../../../services/hooks";

import {
  ControlledFormAutoComplete,
  ControlledFormInput,
  ControlledFormSelect,
  DataGrid,
  SimpleTable,
} from "../../../components";

import { setState } from "../../../services/sessionState";

import {
  getOptionsByProgram,
  getSelectOptionsFromMetadata,
  getUserOptions,
  setTitle,
  sortByLabel,
  sortByName,
  validatePassageName,
} from "../../../services/utils.js";

const CreateContent = () => {
  const client = useClient();

  const [loading, setLoading] = useState(false);
  const [programs, setPrograms] = useState();
  const [accessibleContentAreaValues, setAvailableContentAreaValues] = useState(
    [],
  );
  const [existingPassages, setExistingPassages] = useState([]);
  const [loadingExistingPassages, setLoadingExistingPassages] = useState(false);
  const [itemGroups, setItemGroups] = useState([]);
  const alertContext = useContext(AlertContext);
  const allPrograms = useContext(ProgramsContext);
  const metadataConfiguration = useContext(MetadataConfigurationContext);
  const user = useContext(UserContext);
  const users = useContext(UsersContext);

  const navigate = useNavigate();
  const location = useLocation();

  const {
    control,
    getValues,
    handleSubmit,
    reset,
    resetField,
    setValue,
    watch,
  } = useForm({
    mode: "all",
    reValidateMode: "onBlur",
    defaultValues: defaultValues,
  });
  const { passageName, program } = watch();
  const { isSubmitting, isValid } = useFormState({ control });

  const [mutateItemResponse, mutateItem] = useMutation(createItemSetMutation);

  useEffect(() => {
    setTitle(["CDE", "Create Content"]);
  }, []);

  useEffect(() => {
    if (!allPrograms?.length) return;
    const programOptions = allPrograms
      .filter(({ _actions }) => _actions.includes("createContent"))
      .map(({ configurationID, id: value, name: label }) => {
        return {
          configurationID,
          label,
          value,
        };
      });
    setPrograms(programOptions);
  }, [allPrograms, user?.programAccess]);

  useEffect(() => {
    if (!metadataConfiguration || !program) return;
    const type = passageName ? "passage" : "item";
    const contentAreaOptions = getSelectOptionsFromMetadata(
      metadataConfiguration,
    )(programs.filter(({ value }) => value === program))(type)("contentArea");
    setAvailableContentAreaValues(contentAreaOptions);
  }, [metadataConfiguration, program, passageName]);

  const passageNameOptions = useMemo(
    () => existingPassages.map(({ name }) => name),
    [existingPassages],
  );

  const validationRules = useCallback(
    (inputValue) => {
      return !passageNameOptions.includes(inputValue)
        ? validatePassageName(inputValue)
        : null;
    },
    [passageNameOptions, validatePassageName],
  );

  const passageByProgram = async (programId) => {
    setLoadingExistingPassages(true);
    setExistingPassages([]);
    try {
      const response = await client
        .query(
          searchPassagesByProgramQuery,
          { programId },
          { requestPolicy: "cache-first" },
        )
        .toPromise();
      if (response?.error) throw response.error;
      if (response?.data?.passagesByProgram) {
        setExistingPassages(response.data.passagesByProgram.sort(sortByName));
      }
    } catch (e) {
      console.error("passageByProgram failed", e);
    } finally {
      setLoadingExistingPassages(false);
    }
  };

  useEffect(() => {
    if (program) {
      passageByProgram(program);
    }
  }, [program]);

  const [format] = useGetMetadataConfigurationOptions(
    metadataConfiguration,
    programs,
    "item",
    ["format"],
  );

  if (!programs?.length) return null;

  const createDefaultItem = ({
    passageID,
    passageName,
    program,
    contentArea,
    contentSpecialist,
    itemSets,
  }) => {
    return {
      data: {
        program,
        contentArea,
        contentSpecialist: {
          id: contentSpecialist,
          name: getRHFFieldOptionLabel(contentSpecialistByProgram)(
            contentSpecialist,
          ),
        },
        standards: [],
      },
      itemSets,
      passageID,
      passageName: passageID ? null : passageName,
    };
  };

  const onSubmit = async (formData) => {
    if (!itemGroups.length)
      return alertContext.actions.addAlert({
        message: `Must add at least 1 "Item group" of Item Format and Number to Create!`,
        options: { persist: true, variant: "error" },
      });
    const passageID = formData?.passageName
      ? existingPassages.find(({ name }) => name === formData.passageName)?.id
      : null;
    if (formData?.passageName && !passageID) {
      const response = await client
        .query(passageNameExistsQuery, {
          names: formData?.passageName,
        })
        .toPromise();
      if (response?.data?.doPassageNamesExist[0].found) {
        return alertContext.actions.addAlert({
          message: `Request failed! Passage name ${passageName} already exists in the system`,
          options: { persist: true, variant: "error" },
        });
      } else {
        alertContext.actions.addAlert({
          message: `A new Passage will be created: ${passageName}`,
          options: { persist: true, variant: "info" },
        });
      }
    }

    const itemSets = itemGroups.map(({ format, numberToCreate }) => {
      return {
        item: { format, standards: [] },
        numberToCreate,
      };
    });
    const data = createDefaultItem({
      ...formData,
      passageID,
      itemSets,
    });
    console.info("Item: ", data);
    setLoading(true);
    await mutateItem(data);
    await passageByProgram(program);
    reset({ ...defaultValues });
    setLoading(false);
  };

  const columns = [
    {
      field: "passageName",
      flex: 1,
      headerName: "Passage Name",
      type: "string",
      sortComparator: (v1, v2) =>
        v1.localeCompare(v2, "en", { sensitivity: "base" }),
      valueGetter: getPassageName,
    },
    {
      field: "name",
      flex: 1,
      headerName: "Item Name",
      type: "string",
      sortComparator: (v1, v2) =>
        v1.localeCompare(v2, "en", { sensitivity: "base" }),
    },
    {
      field: "program",
      headerName: "Program",
      sortComparator: (v1, v2) =>
        v1.localeCompare(v2, "en", { sensitivity: "base" }),
      type: "string",
      valueGetter: getProgramName(allPrograms),
      width: 200,
    },
    {
      field: "contentArea",
      headerName: "Content Area",
      sortComparator: (v1, v2) =>
        v1.localeCompare(v2, "en", { sensitivity: "base" }),
      type: "string",
      width: 200,
    },
  ];

  const dataGridProps = {
    autoHeight: true,
    columns,
    error: mutateItemResponse?.error?.message,
    loading,
    rows: mutateItemResponse?.data?.createItemSet?.items.map(
      (itemProperties) => ({
        ...itemProperties,
        passageName: mutateItemResponse?.data?.createItemSet?.passage?.name,
      }),
    ),
    sortModel: [
      {
        field: "passageName",
        sort: "asc",
      },
    ],
  };

  const itemFormats = getOptionsByProgram(allPrograms)(program)(format);

  const createOptions = (availableOptions) => {
    if (!program) return;
    const id = programs.find((p) => p?.value === program)?.configurationID;
    const options = availableOptions
      .filter((option) => option?.configurations.includes(id))
      .map((obj) => ({ label: obj?.stringValue, value: obj?.stringValue }));
    return options;
  };

  const contentSpecialistByProgram = getUserOptions(users)({
    activeOnly: true,
    programs: [program],
    roles: ["Content Specialist"],
  });
  const contentAreas = createOptions(accessibleContentAreaValues);

  const addItemGroup = (itemGroup) => (e) => {
    const re = new RegExp("^[1-9][0-9]?$|^100$");
    if (re.test(itemGroup.numberToCreate)) {
      setItemGroups([...itemGroups, itemGroup]);
    } else {
      alertContext.actions.addAlert({
        message: `This field must contain a valid number 1-100`,
        options: { persist: false, variant: "error" },
      });
    }
  };

  const tableProps = {
    actions: [
      {
        label: "Delete",
        onClick: (row) => (e) => {
          setItemGroups(
            itemGroups.filter((el, idx) => {
              return idx !== row.idx;
            }),
          );
        },
      },
    ],
    columns: [
      { title: "Item Format", identifier: "format" },
      { title: "# to Create", identifier: "numberToCreate" },
    ],
    error: false,
    loading: false,
    rows: itemGroups,
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          margin: 2,
        }}
      >
        <Typography variant="h4">Create Content</Typography>
      </Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Paper
          elevation={1}
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            marginX: 2,
            marginY: 1,
            paddingX: 2,
            paddingY: 1,
          }}
        >
          <Typography sx={{ marginBottom: 2 }} variant="subtitle1">
            Common Metadata
          </Typography>
          <GridGenerator
            groups={[
              [
                {
                  children: (
                    <ControlledFormSelect
                      control={control}
                      fullWidth={true}
                      id={`createContent-program`}
                      label="Program"
                      name="program"
                      options={programs?.sort(sortByLabel)}
                      rules={{
                        required: true,
                      }}
                    />
                  ),
                  key: "createContent-program",
                },
                {
                  children: (
                    <ControlledFormSelect
                      control={control}
                      disabled={!program}
                      fullWidth={true}
                      id="create-content-content-area"
                      label="Content Area"
                      name="contentArea"
                      options={contentAreas}
                      rules={{
                        required: true,
                      }}
                    />
                  ),
                  key: `createContent-format`,
                },
              ],
              [
                {
                  children: (
                    <ControlledFormSelect
                      control={control}
                      disabled={!program}
                      fullWidth={true}
                      id="create-content-content-specialist"
                      label="Content Specialist"
                      name="contentSpecialist"
                      options={contentSpecialistByProgram}
                      rules={{
                        required: true,
                      }}
                    />
                  ),
                  key: `createContent-contentSpecialist`,
                },
                {
                  children: (
                    <ControlledFormAutoComplete
                      control={control}
                      disabled={!program || loadingExistingPassages}
                      freeSolo
                      fullWidth={true}
                      handleHomeEndKeys
                      id="createContent-passageName"
                      inputProps={{
                        endAdornment: (
                          <React.Fragment>
                            {loadingExistingPassages ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                          </React.Fragment>
                        ),
                      }}
                      label="Passage Name"
                      name="passageName"
                      options={existingPassages.map(({ name }) => name)}
                      rules={{
                        validate: validationRules,
                      }}
                      selectOnFocus
                    />
                  ),
                  key: `createContent-passageName`,
                },
              ],
            ]}
          />
          <Box>
            <Button
              color="primary"
              disabled={!program}
              onClick={() =>
                setValue("contentSpecialist", user.id, { shouldValidate: true })
              }
              size="small"
              variant="text"
            >
              Assign to me
            </Button>
          </Box>
        </Paper>
        <Paper
          elevation={1}
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            marginX: 2,
            marginY: 1,
            paddingX: 2,
            paddingY: 1,
          }}
        >
          <Typography sx={{ marginBottom: 2 }} variant="subtitle1">
            Item Metadata
          </Typography>
          <GridGenerator
            groups={[
              [
                {
                  children: (
                    <ControlledFormSelect
                      control={control}
                      fullWidth={true}
                      disabled={!program}
                      id="create-content-format"
                      label="Item Format"
                      name="format"
                      options={itemFormats}
                      rules={{
                        required: true,
                      }}
                    />
                  ),
                  key: `createContent-format`,
                },
                {
                  children: (
                    <ControlledFormInput
                      control={control}
                      id="number-to-create"
                      label="Number To Create"
                      name="numberToCreate"
                      rules={{
                        maxLength: 3,
                        pattern: {
                          message:
                            "This field must contain a valid number 1-100",
                          value: /^[1-9][0-9]?$|^100$/,
                        },
                      }}
                    />
                  ),
                  key: `createContent-numberToCreate`,
                },
                {
                  children: (
                    <Box
                      sx={{
                        alignItems: "center",
                        display: "flex",
                        height: "100%",
                      }}
                    >
                      <Button
                        onClick={addItemGroup({
                          format: getValues("format"),
                          numberToCreate: getValues("numberToCreate"),
                        })}
                        size="small"
                        startIcon={<PlusIcon />}
                        sx={{ marginRight: 2 }}
                        variant="outlined"
                      >
                        Add
                      </Button>
                    </Box>
                  ),
                  key: `createContent-addItemGroup`,
                },
              ],
            ]}
          />
        </Paper>
        <Paper
          elevation={1}
          sx={{
            display: "flex",
            flexGrow: 1,
            justifyContent: "space-between",
            marginX: 2,
            marginY: 1,
          }}
        >
          <Box sx={{ flexGrow: 1, marginRight: 2 }}>
            <SimpleTable {...tableProps} isCreateContent={true} />
          </Box>
          <Box>
            <Button
              onClick={() => {
                reset({ ...defaultValues });
                setExistingPassages([]);
                setItemGroups([]);
                mutateItemResponse.data = null;
              }}
              sx={{ marginRight: 2 }}
              variant="outlined"
            >
              Reset
            </Button>
            <Button
              disabled={!itemGroups.length || isSubmitting || !isValid}
              sx={{ marginRight: 2 }}
              type="submit"
              variant="contained"
            >
              Create
            </Button>
          </Box>
        </Paper>
      </form>
      {(mutateItemResponse?.data || loading) && (
        <Paper
          elevation={1}
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            marginX: 2,
            marginY: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              marginX: 2,
              marginY: 1,
            }}
          >
            <Typography variant="h5">Results</Typography>
            <Button
              onClick={() => {
                const itemList =
                  mutateItemResponse?.data?.createItemSet?.items.map((item) => {
                    return item.name;
                  });
                setState("itemSearchFilters")({ itemList });
                navigate("/search/items", {
                  state: { from: location.pathname },
                });
              }}
              size="small"
              variant="outlined"
            >
              View in Search
            </Button>
          </Box>
          <Box sx={{ display: "flex", width: "100%" }}>
            <DataGrid {...dataGridProps}></DataGrid>
          </Box>
        </Paper>
      )}
    </>
  );
};

const createItemSetMutation = `
  mutation CreateItemSet($data: PassageInput, $passageName: String, $passageID: ID, $itemSets: [ItemSetInput!]!) { 
      createItemSet(data: $data, passageName: $passageName, passageID: $passageID, itemSets: $itemSets) { 
        items {
          contentArea
          contentSpecialist {
            id
          }
          developmentState
          id
          itemWriter {
            id
          }
          name
          program
        }
        passage {
          contentSpecialist {
            id
          }
          developmentState
          id
          itemWriter {
            id
          }
          name
          program
        } 
      } 
  }
`;

const searchPassagesByProgramQuery = `
  query searchPassagesByProgram($programId: ID!) {
    passagesByProgram(programId: $programId) {
      id
      name
    }
  }
`;

const passageNameExistsQuery = `
  query PassageNameExists($names: [String!]!) {
    doPassageNamesExist(names: $names) {
      found
      name
    }
  }
`;

const defaultValues = {
  contentArea: "",
  contentSpecialist: "",
  format: "Selected Response",
  numberToCreate: 1,
  passageName: "",
  program: "",
};
const getPassageName = (params) => `${params.row?.passageName || ""}`;
const getProgramName = (allPrograms) => (params) => {
  return allPrograms.find(({ id }) => id === params.row?.program)?.name || "";
};

export { CreateContent, createItemSetMutation, passageNameExistsQuery };
